import React, { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { formatTemplateToken } from '@cotera/utilities';
import { Icon, Tooltip } from '@cotera/client/app/components';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@cotera/client/v0/components/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ToolCallUpstreamSample } from '../../../hooks/use-tool-call-upstream-samples';
import {
  classifyToolInputField,
  type ToolInputField,
} from './tool-input-schema';
import {
  refIssuesForField,
  type ToolInputRefIssue,
} from './tool-input-ref-issues';
import { RefIssueNotice } from './ref-issue-notice';
import {
  buildUpstreamSources,
  type UpstreamSourceGroup,
} from './variable-picker-data';
import { TokenInput } from './tiptap/token-input';
import {
  TOOL_INPUT_CONTROL_HEIGHT,
  TOOL_INPUT_JSON_CLASS,
} from './tool-input-styles';
import {
  INLINE_PICKER_INSET,
  UpstreamRefChip,
  UpstreamRefPickerButton,
} from './upstream-ref-picker';
import {
  alwaysShownToolInputFields,
  hiddenToolInputFields,
  resolveToolInputControl,
  staleParamsAfterChange,
  toolInputFieldLabel,
} from './tool-input-renderers';

type ParamValues = Record<string, unknown>;
type ParamBindings = Record<string, string>;

export type ToolInputsEditorProps = {
  fields: readonly ToolInputField[];
  params: ParamValues;
  bindings: ParamBindings;
  upstreamSamples: readonly ToolCallUpstreamSample[];
  /**
   * Refs saved on this node that no longer read anything, resolved by the
   * editor above so the same list can both be shown here and block the save.
   */
  refIssues: readonly ToolInputRefIssue[];
  onChange: (next: { params: ParamValues; bindings: ParamBindings }) => void;
  /**
   * Which tool these inputs belong to, so a tool that needs a purpose-built
   * control for one of its fields can supply one. Optional: without it every
   * field renders off its type, which is the right default.
   */
  toolKey?: string;
};

/** camelCase / snake_case field name -> lowercase words, for placeholders. */
function humanize(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * A short field hint for the placeholder. Tool descriptions often read
 * "Short label (long examples…)", so take the label; the full description
 * stays in the field's info-icon tooltip.
 */
function placeholderFor(field: ToolInputField): string {
  const description = field.description?.trim();
  if (description !== undefined && description !== '') {
    const short = description.split(/\s+\(/)[0]?.trim() ?? '';
    if (short !== '' && short.length <= 60) {
      return short;
    }
    return `${description.slice(0, 57).trimEnd()}…`;
  }
  return `Enter ${humanize(field.name)}`;
}

/** Text-like fields get a taller, multi-line token input. */
function isMultilineField(field: ToolInputField): boolean {
  return /(text|body|message|content|description|markdown|prompt)/i.test(
    field.name
  );
}

/** A field is configured if it has a static value or an upstream binding. */
export function isToolInputConfigured(
  name: string,
  params: ParamValues,
  bindings: ParamBindings
): boolean {
  if (bindings[name] !== undefined) {
    return true;
  }
  const value = params[name];
  // An empty list is not a value. A control that builds a list writes one as
  // soon as it is mounted, and counting that as configured let a node save with
  // nothing in it — which only failed once the tool ran and refused it.
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value !== undefined && value !== '';
}

/** Names of required fields still missing a value or binding. */
export function missingRequiredInputs(
  fields: readonly ToolInputField[],
  params: ParamValues,
  bindings: ParamBindings
): string[] {
  return fields
    .filter(
      (field) =>
        field.required && !isToolInputConfigured(field.name, params, bindings)
    )
    .map((field) => field.name);
}

// ---------------------------------------------------------------------------
// Native controls (non-string static values)
// ---------------------------------------------------------------------------

const NumberField: React.FC<{
  integer: boolean;
  inset: string | undefined;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ integer, inset, value, onChange }) => {
  const [text, setText] = useState(
    typeof value === 'number' ? String(value) : ''
  );
  return (
    <Input
      className={cn(TOOL_INPUT_CONTROL_HEIGHT, 'w-full', inset)}
      inputMode={integer ? 'numeric' : 'decimal'}
      placeholder={integer ? 'Enter a whole number' : 'Enter a number'}
      value={text}
      onChange={(event) => {
        const next = event.target.value;
        setText(next);
        if (next === '') {
          onChange(undefined);
          return;
        }
        const parsed = Number(next);
        if (Number.isNaN(parsed)) {
          return;
        }
        onChange(integer ? Math.trunc(parsed) : parsed);
      }}
    />
  );
};

const JsonField: React.FC<{
  inset: string | undefined;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ inset, value, onChange }) => {
  // `null` is a value the tools use for "unset" (a dataset read with no
  // `columns` reads them all). Rendering it as the literal `null` put a word in
  // a box that reads as something the user typed; the placeholder says more.
  const [text, setText] = useState(() =>
    value === undefined || value === null ? '' : JSON.stringify(value, null, 2)
  );
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-1">
      <textarea
        className={cn(TOOL_INPUT_JSON_CLASS, inset)}
        placeholder="Enter JSON"
        spellCheck={false}
        value={text}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          if (next.trim() === '') {
            setError(null);
            onChange(undefined);
            return;
          }
          try {
            const parsed: unknown = JSON.parse(next);
            setError(null);
            onChange(parsed);
          } catch {
            setError('Invalid JSON');
          }
        }}
      />
      {error !== null ? (
        <Text.Caption className="text-destructive block">{error}</Text.Caption>
      ) : null}
    </div>
  );
};

const NativeControl: React.FC<{
  field: ToolInputField;
  /** Set when an inline picker sits inside this control's box. */
  inset: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ field, inset, value, onChange }) => {
  const classified = classifyToolInputField(field.schema);
  const pad = inset ? INLINE_PICKER_INSET : undefined;
  switch (classified.kind) {
    case 'number':
      return (
        <NumberField
          integer={classified.integer}
          inset={pad}
          value={value}
          onChange={onChange}
        />
      );
    case 'boolean':
      return (
        <Select
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onValueChange={(next) => onChange(next === 'true')}
        >
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
          </SelectContent>
        </Select>
      );
    case 'enum':
      return (
        <Select
          value={typeof value === 'string' ? value : ''}
          onValueChange={(next) => onChange(next)}
        >
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {classified.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'timestamp':
      return (
        <Input
          type="datetime-local"
          className={cn(TOOL_INPUT_CONTROL_HEIGHT, 'w-full', pad)}
          value={
            typeof value === 'string' && value.endsWith('Z')
              ? value.slice(0, -1)
              : ''
          }
          onChange={(event) =>
            onChange(
              event.target.value === '' ? undefined : `${event.target.value}Z`
            )
          }
        />
      );
    default:
      return <JsonField inset={pad} value={value} onChange={onChange} />;
  }
};

// ---------------------------------------------------------------------------
// Field row
// ---------------------------------------------------------------------------

const FieldHeader: React.FC<{
  field: ToolInputField;
  toolKey?: string;
  onRemove?: () => void;
}> = ({ field, toolKey, onRemove }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="flex min-w-0 items-center gap-1.5">
      <Text.Caption className="text-foreground truncate font-medium">
        {toolInputFieldLabel(toolKey, field.name)}
      </Text.Caption>
      {field.required ? (
        <span className="text-destructive text-xs">*</span>
      ) : null}
      {field.description !== null ? (
        <Tooltip side="top" tooltipContent={field.description}>
          <span className="flex shrink-0 cursor-pointer items-center">
            <Icon icon="information-circle" className="text-muted-foreground" />
          </span>
        </Tooltip>
      ) : null}
    </span>
    {onRemove !== undefined ? (
      <button
        type="button"
        onClick={onRemove}
        title="Remove input"
        aria-label={`Remove ${toolInputFieldLabel(toolKey, field.name)}`}
        className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-1 flex size-6 shrink-0 items-center justify-center rounded transition-colors"
      >
        <X className="size-3.5" />
      </button>
    ) : null}
  </div>
);

const ToolInputRow: React.FC<{
  field: ToolInputField;
  params: ParamValues;
  bindings: ParamBindings;
  sources: readonly UpstreamSourceGroup[];
  sourceLabels: ReadonlyMap<string, string>;
  refIssues: readonly ToolInputRefIssue[];
  onRemove?: () => void;
  toolKey?: string;
  setString: (name: string, template: string) => void;
  setNativeValue: (name: string, value: unknown) => void;
  setValues: (patch: Readonly<Record<string, unknown>>) => void;
  setBinding: (name: string, ref: string) => void;
  clearBinding: (name: string) => void;
}> = ({
  field,
  params,
  bindings,
  sources,
  sourceLabels,
  refIssues,
  onRemove,
  toolKey,
  setString,
  setNativeValue,
  setValues,
  setBinding,
  clearBinding,
}) => {
  const kind = classifyToolInputField(field.schema).kind;
  const boundRef = bindings[field.name];
  const CustomControl = resolveToolInputControl(toolKey, field.name);
  const fieldIssues = refIssuesForField(refIssues, field.name);

  const control = ((): React.ReactNode => {
    // A tool-supplied control replaces the generic one outright — including the
    // upstream-binding affordance, which it offers itself where that makes
    // sense for the field.
    if (CustomControl !== null) {
      return (
        <CustomControl
          field={field}
          params={params}
          paramBindings={bindings}
          sources={sources}
          refIssues={fieldIssues}
          setValues={(patch) =>
            setValues({
              ...staleParamsAfterChange({
                field: field.name,
                patch,
                params,
                toolKey,
              }),
              ...patch,
            })
          }
        />
      );
    }
    if (kind === 'string') {
      const paramValue = params[field.name];
      const initial =
        typeof paramValue === 'string'
          ? paramValue
          : boundRef !== undefined
          ? formatTemplateToken(boundRef)
          : '';
      return (
        <TokenInput
          value={initial}
          multiline={isMultilineField(field)}
          placeholder={placeholderFor(field)}
          sources={sources}
          onChange={(template) => setString(field.name, template)}
        />
      );
    }
    // Non-string: native control, with an upstream-reference escape hatch.
    if (boundRef !== undefined) {
      return (
        <UpstreamRefChip
          refValue={boundRef}
          sourceLabels={sourceLabels}
          onRemove={() => clearBinding(field.name)}
          removeTitle="Use a fixed value instead"
        />
      );
    }
    // A select's own chevron already owns the corner the inline picker wants,
    // so those two keep it alongside; every other box hosts it the way the
    // token input does.
    const selectLike = kind === 'boolean' || kind === 'enum';
    if (selectLike) {
      return (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <NativeControl
              field={field}
              inset={false}
              value={params[field.name]}
              onChange={(value) => setNativeValue(field.name, value)}
            />
          </div>
          <UpstreamRefPickerButton
            sources={sources}
            onPick={(ref) => setBinding(field.name, ref)}
          />
        </div>
      );
    }
    return (
      <div className="relative">
        <NativeControl
          field={field}
          inset={sources.length > 0}
          value={params[field.name]}
          onChange={(value) => setNativeValue(field.name, value)}
        />
        <UpstreamRefPickerButton
          variant="inline"
          sources={sources}
          onPick={(ref) => setBinding(field.name, ref)}
        />
      </div>
    );
  })();

  return (
    <div className="flex flex-col gap-1.5">
      <FieldHeader field={field} toolKey={toolKey} onRemove={onRemove} />
      {control}
      {/* A control that draws its own boxes places these itself, against the
          box the bad ref is actually in. Everything else gets them here. */}
      {CustomControl === null ? <RefIssueNotice issues={fieldIssues} /> : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

export const ToolInputsEditor: React.FC<ToolInputsEditorProps> = ({
  fields,
  params,
  bindings,
  upstreamSamples,
  refIssues,
  onChange,
  toolKey,
}) => {
  const sources = useMemo(
    () => buildUpstreamSources(upstreamSamples),
    [upstreamSamples]
  );
  const sourceLabels = useMemo(
    () =>
      new Map(
        upstreamSamples.map(
          (sample) => [sample.sourceName, sample.label] as const
        )
      ),
    [upstreamSamples]
  );
  const [addedOptionals, setAddedOptionals] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [addOpen, setAddOpen] = useState(false);

  const setString = (name: string, template: string): void => {
    const nextParams = { ...params };
    const nextBindings = { ...bindings };
    delete nextBindings[name];
    if (template === '') {
      delete nextParams[name];
    } else {
      nextParams[name] = template;
    }
    onChange({ params: nextParams, bindings: nextBindings });
  };

  const setNativeValue = (name: string, value: unknown): void => {
    const nextParams = { ...params };
    const nextBindings = { ...bindings };
    delete nextBindings[name];
    if (value === undefined) {
      delete nextParams[name];
    } else {
      nextParams[name] = value;
    }
    onChange({ params: nextParams, bindings: nextBindings });
  };

  /**
   * Several params in one edit.
   *
   * A control that owns more than one field cannot call `setNativeValue` twice:
   * both calls build from the `params` of the render that produced them, so the
   * second would drop the first's write.
   */
  const setValues = (patch: Readonly<Record<string, unknown>>): void => {
    const nextParams = { ...params };
    const nextBindings = { ...bindings };
    for (const [name, value] of Object.entries(patch)) {
      delete nextBindings[name];
      if (value === undefined) {
        delete nextParams[name];
      } else {
        nextParams[name] = value;
      }
    }
    onChange({ params: nextParams, bindings: nextBindings });
  };

  const setBinding = (name: string, ref: string): void => {
    const nextParams = { ...params };
    delete nextParams[name];
    onChange({ params: nextParams, bindings: { ...bindings, [name]: ref } });
  };

  const clearBinding = (name: string): void => {
    const nextBindings = { ...bindings };
    delete nextBindings[name];
    onChange({ params, bindings: nextBindings });
  };

  const clearField = (name: string): void => {
    const nextParams = { ...params };
    const nextBindings = { ...bindings };
    delete nextParams[name];
    delete nextBindings[name];
    setAddedOptionals((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
    onChange({ params: nextParams, bindings: nextBindings });
  };

  // Fields one of this tool's own controls maintains. Rendering them again as
  // generic inputs would show the same value twice and let the two disagree.
  const owned = hiddenToolInputFields(toolKey);
  const alwaysShow = alwaysShownToolInputFields(toolKey);
  const shown = fields.filter((field) => !owned.has(field.name));
  // Primary: the fields shown without being asked for — required ones, plus the
  // optional ones the tool pins up front (its dataset picker and builder).
  const primaryFields = shown.filter(
    (field) => field.required || alwaysShow.has(field.name)
  );
  const optionalFields = shown.filter(
    (field) => !field.required && !alwaysShow.has(field.name)
  );
  const visibleOptional = optionalFields.filter(
    (field) =>
      addedOptionals.has(field.name) ||
      isToolInputConfigured(field.name, params, bindings)
  );
  const hiddenOptional = optionalFields.filter(
    (field) => !visibleOptional.includes(field)
  );

  const renderRow = (
    field: ToolInputField,
    removable: boolean
  ): React.ReactNode => (
    <ToolInputRow
      key={field.name}
      field={field}
      params={params}
      bindings={bindings}
      sources={sources}
      sourceLabels={sourceLabels}
      refIssues={refIssues}
      onRemove={removable ? () => clearField(field.name) : undefined}
      toolKey={toolKey}
      setString={setString}
      setValues={setValues}
      setNativeValue={setNativeValue}
      setBinding={setBinding}
      clearBinding={clearBinding}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <Text.Caption className="text-muted-foreground block">
        Type a value, or press <span className="font-mono">/</span> to insert
        data from a previous step.
      </Text.Caption>

      {primaryFields.map((field) => renderRow(field, false))}

      {visibleOptional.length > 0 ? (
        <>
          <div className="flex items-center gap-2">
            <div className="bg-border h-px flex-1" />
            <Text.Caption className="text-muted-foreground/70 text-[10px] uppercase tracking-wide">
              Optional
            </Text.Caption>
            <div className="bg-border h-px flex-1" />
          </div>
          {visibleOptional.map((field) => renderRow(field, true))}
        </>
      ) : null}

      {hiddenOptional.length > 0 ? (
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
            >
              <Plus className="mr-1 size-3.5" />
              Add input
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[280px] p-0">
            <Command>
              <CommandInput placeholder="Add an input…" />
              <CommandList>
                <CommandEmpty>No more inputs.</CommandEmpty>
                <CommandGroup>
                  {hiddenOptional.map((field) => (
                    <CommandItem
                      key={field.name}
                      value={field.name}
                      onSelect={() => {
                        setAddedOptionals((prev) =>
                          new Set(prev).add(field.name)
                        );
                        setAddOpen(false);
                      }}
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">
                          {toolInputFieldLabel(toolKey, field.name)}
                        </span>
                        <span className="text-muted-foreground truncate text-[11px]">
                          {field.description ?? field.typeLabel}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
};
